import React, { useState } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function AdminDuplicateUserModal({ user, open, onClose, onCreated }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [emailError, setEmailError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!open || !user) {
    return null;
  }

  const emailOk = EMAIL_PATTERN.test(email.trim());
  const passwordOk = password.length >= 6 && password.length <= 72;
  const confirmOk = passwordConfirm === password;
  const canSubmit = emailOk && passwordOk && confirmOk && !submitting;

  const clearFields = () => {
    setEmail("");
    setPassword("");
    setPasswordConfirm("");
    setEmailError("");
  };

  const handleClose = () => {
    if (submitting) {
      return;
    }
    clearFields();
    onClose();
  };

  const handleSubmit = async () => {
    if (!canSubmit) {
      return;
    }

    setSubmitting(true);
    setEmailError("");
    try {
      const token = Cookies.get("token");
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/users/${user.id}/duplicate`,
        { email: email.trim().toLowerCase(), password },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success("Client duplicated");
      onCreated?.(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409) {
        setEmailError(typeof detail === "string" ? detail : "");
      } else {
        toast.error(typeof detail === "string" && detail ? detail : "Failed to duplicate user");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="admin-duplicate-user-title"
    >
      <div className="w-full max-w-md rounded-xl border border-white/10 bg-[#0F1115] p-6 shadow-xl">
        <h2 id="admin-duplicate-user-title" className="font-heading text-lg font-semibold text-white">
          Duplicate user
        </h2>
        <p className="mt-3 text-sm text-[#9CA3AF]">
          This creates a new client with the same bot settings. Billing, integrations, and chats stay on the original account.
        </p>
        <label className="mt-4 block text-sm text-[#9CA3AF]" htmlFor="admin-duplicate-email">
          Email
        </label>
        <input
          id="admin-duplicate-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mt-2 w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white"
          autoComplete="off"
          data-testid="admin-duplicate-email"
        />
        {emailError ? (
          <p className="mt-2 text-sm text-red-400">{emailError}</p>
        ) : null}
        <label className="mt-4 block text-sm text-[#9CA3AF]" htmlFor="admin-duplicate-password">
          Password
        </label>
        <input
          id="admin-duplicate-password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          maxLength={72}
          autoComplete="new-password"
          className="mt-2 w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white"
          data-testid="admin-duplicate-password"
        />
        <label className="mt-4 block text-sm text-[#9CA3AF]" htmlFor="admin-duplicate-password-confirm">
          Repeat password
        </label>
        <input
          id="admin-duplicate-password-confirm"
          type="password"
          value={passwordConfirm}
          onChange={(e) => setPasswordConfirm(e.target.value)}
          maxLength={72}
          autoComplete="new-password"
          className="mt-2 w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white"
          data-testid="admin-duplicate-password-confirm"
        />
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            className="px-4 py-2 rounded-lg border border-white/10 text-sm text-[#9CA3AF] hover:bg-white/5 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            data-testid="admin-duplicate-submit"
            className="px-4 py-2 rounded-lg border border-[#00D1FF]/30 text-[#00D1FF] text-sm hover:bg-[#00D1FF]/10 disabled:opacity-50"
          >
            {submitting ? "Creating…" : "Create duplicate"}
          </button>
        </div>
      </div>
    </div>
  );
}
